<template>
  <div class="audience-segmentation">
    <h2 class="audience-segmentation__title">{{title}}</h2>

    <div class="audience-segmentation-explanation">
      <div class="audience-segmentation-explanation__wrapper">
        <div class="audience-segmentation-explanation__image-wrapper audience-segmentation-explanation__image-wrapper--left">
          <span class="audience-segmentation-explanation__text audience-segmentation-explanation__text--negative">01</span>
          <img class="audience-segmentation-explanation__image" src="@/assets/images/sad-face.svg" />
        </div>
        <span class="audience-segmentation-explanation__label">Strongly Disagree</span>
      </div>

      <div class="audience-segmentation-explanation__wrapper">
        <img class="audience-segmentation-explanation__icon" src="@/assets/images/icon/double-arrow.svg" />
      </div>

      <div class="audience-segmentation-explanation__wrapper">
        <div class="audience-segmentation-explanation__image-wrapper audience-segmentation-explanation__image-wrapper--right">
          <img class="audience-segmentation-explanation__image" src="@/assets/images/happy-face.svg" />
          <span class="audience-segmentation-explanation__text audience-segmentation-explanation__text--positive">10</span>
        </div>
        <span class="audience-segmentation-explanation__label">Strongly Agree</span>
      </div>
    </div>

    <div class="tla-form-group tla-form-group--margin-large">
      <label class="tla-form-group__label tla-form-group__label--required" v-html="question1Label" />

      <NumberInput
        id="audienceSegmentationQuestion1"
        name="audienceSegmentationQuestion1"
        v-model="questionAnswer1"
        :min="0"
        :max="10"
        :step="1"
        :prefix="''"
        :precision="0"
        :slider="true"
        :fieldName="'audienceSegmentationQuestion1'"
        :controls="false"
        data-vv-name="audienceSegmentationQuestion1"
        :data-vv-scope="validationScope"
        v-validate.initial="'min_value:1|max_value:10'"
        @audienceSegmentationQuestion1="questionAnswer1 = $event">
      </NumberInput>
      <span class="tla-form-group__message" :class="getValidationClass('audienceSegmentationQuestion1', 'message', questionAnswer1Changed)">{{ errors.first(`${validationScope}.audienceSegmentationQuestion1`) }}</span>
      <vue-slider v-if="!hideSliders" v-model="questionAnswer1" :min="0" :max="10" height="10" :dot-size="26" :tooltip="false" :interval="1" :class="{ 'vue-slider-component--no-value': questionAnswer1 === 0 }"></vue-slider>
    </div>

    <div class="tla-form-group tla-form-group--margin-large">
      <label class="tla-form-group__label tla-form-group__label--required" v-html="question2Label" />

      <NumberInput
        id="audienceSegmentationQuestion2"
        name="audienceSegmentationQuestion2"
        v-model="questionAnswer2"
        :min="0"
        :max="10"
        :step="1"
        :prefix="''"
        :precision="0"
        :slider="true"
        :fieldName="'audienceSegmentationQuestion2'"
        :controls="false"
        data-vv-name="audienceSegmentationQuestion2"
        :data-vv-scope="validationScope"
        v-validate.initial="'min_value:1|max_value:10'"
        @audienceSegmentationQuestion2="questionAnswer2 = $event">
      </NumberInput>
      <span class="tla-form-group__message" :class="getValidationClass('audienceSegmentationQuestion2', 'message', questionAnswer2Changed)">{{ errors.first(`${validationScope}.audienceSegmentationQuestion2`) }}</span>
      <vue-slider  v-if="!hideSliders" v-model="questionAnswer2" :min="0" :max="10" height="10" :dot-size="26" :tooltip="false" :interval="1" :class="{ 'vue-slider-component--no-value': questionAnswer2 === 0 }"></vue-slider>
    </div>

    <div class="tla-form-group tla-form-group--margin-large">
      <label class="tla-form-group__label tla-form-group__label--required" v-html="question3Label" />

      <NumberInput
        id="audienceSegmentationQuestion3"
        name="audienceSegmentationQuestion3"
        v-model="questionAnswer3"
        :min="0"
        :max="10"
        :step="1"
        :prefix="''"
        :precision="0"
        :slider="true"
        :fieldName="'audienceSegmentationQuestion3'"
        :controls="false"
        data-vv-name="audienceSegmentationQuestion3"
        :data-vv-scope="validationScope"
        v-validate.initial="'min_value:1|max_value:10'"
        @audienceSegmentationQuestion3="questionAnswer3 = $event">
      </NumberInput>
      <span class="tla-form-group__message" :class="getValidationClass('audienceSegmentationQuestion3', 'message', questionAnswer3Changed)">{{ errors.first(`${validationScope}.audienceSegmentationQuestion3`) }}</span>
      <vue-slider v-if="!hideSliders" v-model="questionAnswer3" :min="0" :max="10" height="10" :dot-size="26" :tooltip="false" :interval="1" :class="{ 'vue-slider-component--no-value': questionAnswer3 === 0 }" />
    </div>

      <div class="tla-form-group">
        <label class="tla-form-group__label tla-form-group__label--block tla-form-group__label--required" v-html="question4Label" />
        <label for="question4" class="tla-form-group__label tla-form-group__label--required">{{question4Title}}</label>
        <select
          id="question4"
          v-model="questionAnswer4"
          v-validate.initial="'required'"
          name="question4"
          data-vv-name="question4"
          :data-vv-as="`${question4Title.toLowerCase()} question`"
          :data-vv-scope="validationScope"
          class="tla-form-group__input tla-form-group__input--arrow"
          :class="getValidationClass('question4', 'input')"
        >
          <option selected disabled :value="null">
            Please select an option
          </option>
          <option v-for="option in questionOptions" :key="option.key" :value="option.key">
            {{ option.value }}
          </option>
          <optgroup class="audience-segmentation__optgroup" label=""></optgroup> /* Fix for iOS wrapping text */
        </select>
        <span class="tla-form-group__message tla-form-group__message--spaced" :class="getValidationClass('question4', 'message')">{{ errors.first(`${validationScope}.question4`) }}</span>
      </div>
  </div>
</template>

<script lang="ts">
import {
  Component, Prop, Vue, Watch
} from 'vue-property-decorator';
import VeeValidate from 'vee-validate';
import vueSlider from 'vue-slider-component';
import { audienceSegmentationService } from '@/services/AudienceSegmentationService';
import IAudienceSegmentQuestionResponse from '@/models/interfaces/IAudienceSegmentQuestionResponse';
import ExtraData from '@/models/ExtraData';
import KeyValue from '@/models/KeyValue';
import { validationService } from '@/services/ValidationService';
import AudienceSegmentationMessages from '@/validation/audience-segmentation';
import mobileCheckService from '@/services/MobileCheckService';
import NumberInput from '../widgets/common/NumberInput.vue';

Vue.use(VeeValidate, AudienceSegmentationMessages);

@Component({
  components: {
  NumberInput,
  vueSlider
  }
  })
export default class AudienceSegmentationComponent extends Vue {
  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: 'To what extent do you agree with the following statements?' })
  public title!: string;

  @Prop({ default: 'My car should express my personality <span>*</span>' })
  public question1Label!: string;

  @Prop({ default: 'My car is only to get me from \'A\' to \'B\' <span>*</span>' })
  public question2Label!: string;

  @Prop({ default: 'I am always looking for ways to reduce my impact on the environment <span>*</span>' })
  public question3Label!: string;

  @Prop({ default: 'To what extent would you consider the following method of transport? <span>*</span>' })
  public question4Label!: string;

  @Prop({ default: 'Driverless cars' })
  public question4Title!: string;

  public questionOptions: KeyValue[] = [
    {
      key: '1',
      value: 'I would not consider this'
    },
    {
      key: '2',
      value: 'I would only consider if it was more convenient'
    },
    {
      key: '3',
      value: 'I would only consider if it was safer than other options'
    },
    {
      key: '4',
      value: 'I would only consider if it was cheaper than other options'
    },
    {
      key: '5',
      value: 'I would consider this'
    }
  ];

  private questionAnswer1: number = 0;

  private questionAnswer2: number = 0;

  private questionAnswer3: number = 0;

  private questionAnswer4: string | null = null;

  private questionAnswer1Changed: boolean = false;

  private questionAnswer2Changed: boolean = false;

  private questionAnswer3Changed: boolean = false;

  private hideSliders: boolean = false;

  private get questionAnswers(): IAudienceSegmentQuestionResponse[] {
    return [
      {
        id: 1,
        value: this.questionAnswer1
      },
      {
        id: 2,
        value: this.questionAnswer2
      },
      {
        id: 3,
        value: this.questionAnswer3
      },
      {
        id: 4,
        value: this.questionAnswer4 ? Number.parseInt(this.questionAnswer4, 10) : 0
      }
    ];
  }

  @Watch('questionAnswer1')
  public onQuestionAnswer1Changed(value: number, oldValue: number) {
    this.questionAnswer1Changed = value !== oldValue;
  }

  @Watch('questionAnswer2')
  public onQuestionAnswer2Changed(value: number, oldValue: number) {
    this.questionAnswer2Changed = value !== oldValue;
  }

  @Watch('questionAnswer3')
  public onQuestionAnswer3Changed(value: number, oldValue: number) {
    this.questionAnswer3Changed = value !== oldValue;
  }

  @Watch('questionAnswers', { deep: true })
  private async questionAnswersUpdated(values: IAudienceSegmentQuestionResponse[]) {
    const hasCompletedAnswers = values.every((x) => x.value !== 0);

    if (hasCompletedAnswers) {
      this.submitAnswers(this.questionAnswers);
    }
  }

  private submitAnswers(responses: IAudienceSegmentQuestionResponse[]) {
    const result = audienceSegmentationService.getAudienceSegment(responses);

    if (!result) {
      return;
    }

    responses.forEach((response) => {
      this.$store.dispatch('enquiryModule/submitExtraData', new ExtraData(`audienceSegmentationAnswer${response.id}`, response.value.toString()));
    });

    this.$store.dispatch('enquiryModule/submitExtraData', new ExtraData('audienceSegmentationResult', result.value.toString()));
    this.$store.dispatch('enquiryModule/submitExtraData', new ExtraData('audienceSegmentationResultName', result.type));
  }

  public getValidationClass(fieldName: string, fieldType?: string, fieldChanged?: boolean) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, fieldChanged, false, this.validationScope);
  }

  public created() {
    this.$validator.localize('en', AudienceSegmentationMessages.english);
    this.hideSliders = mobileCheckService.hideSliders;
  }
}
</script>
